import { booleanPointInPolygon, point, type MultiPolygon } from '@turf/turf';
import React from 'react';
import { useForm } from 'react-hook-form';
import { ControlledInputField } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-input-field';
import { SupportRequestModalForm } from 'features/offline-reports/support-requests/components/support-requests-modals/components/support-request-modal-form';
import { Banner, Callout, FlexContainer, Link, UtilityInfoIcon } from '@placer-ui/components';
import { SupportEmailInput } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';
import { getFeatureFlags } from 'core/flow-control';
import type { SupportRequestsData } from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import {
    COORDINATES_ERROR_MESSAGES,
    LAT_REGEX,
    LONG_REGEX,
} from 'features/offline-reports/support-requests/consts';

import styles from './requests-traffic-pin.module.scss';
import polygon_us from './polygon_us.json';

interface RequestTrafficPinFormValues {
    type: 'traffic_pin';
    lat: number;
    lng: number;
    address: string;
    executiveEmail?: string;
    opportunityUrl?: string;
}

const BANNER_TEXT =
    'Please provide coordinates alongside the specific road segment where you’d like to capture traffic.';

export const RequestTrafficPin = (props: SupportRequestsData['trafficPin']) => {
    const { enable_request_properties_sales_flow_ff, enable_tf_error_outside_usa } =
        getFeatureFlags();

    const formController = useForm<RequestTrafficPinFormValues>({
        defaultValues: {
            type: 'traffic_pin',
            lat: undefined,
            lng: undefined,
            ...(props ?? {}),
        },
        mode: 'all',
    });

    const coordinatesValidator = (
        value: number | string | undefined,
        { lat, lng }: RequestTrafficPinFormValues,
    ) => {
        if (!lat || !lng) return true;

        const pin = point([Number(lng), Number(lat)]);

        if (!booleanPointInPolygon(pin, polygon_us as MultiPolygon)) {
            formController.setError('root.outsideUS', {
                type: 'manual',
                message: COORDINATES_ERROR_MESSAGES.outsideUS,
            });
            return false;
        } else {
            formController.clearErrors('root.outsideUS');
            return true;
        }
    };

    const outsideUSError = formController.formState.errors.root?.outsideUS;

    return (
        <SupportRequestModalForm<RequestTrafficPinFormValues>
            testId="request-traffic-pin"
            requestType="trafficPin"
            title="Request a Traffic Pin"
            subTitle="Provide coordinates to add billboards or similar points of interest"
            form={formController}
            convertToRequestPayload={(values) => values}
        >
            <div className={styles.contentContainer}>
                <FlexContainer gap={8} flexDirection="column">
                    <Banner testId="traffic-pin-banner" text={BANNER_TEXT} type="primary" />
                    {enable_request_properties_sales_flow_ff && (
                        <FlexContainer gap={16}>
                            <SupportSalesEmailInput />
                            <SupportOpportunityUrlInput />
                        </FlexContainer>
                    )}
                    {!enable_request_properties_sales_flow_ff && <SupportEmailInput />}
                    <div className={styles.formContainer}>
                        <div>
                            <ControlledInputField
                                control={formController.control}
                                name="lat"
                                label="Latitude"
                                data-testid="lat-input"
                                placeholder="e.g. 40.6892"
                                type="number"
                                asterix
                                required
                                maxLength={11}
                                rules={{
                                    pattern: {
                                        value: LAT_REGEX,
                                        message: COORDINATES_ERROR_MESSAGES.lat,
                                    },
                                    validate: enable_tf_error_outside_usa
                                        ? coordinatesValidator
                                        : undefined,
                                }}
                                globalError={outsideUSError}
                            />
                        </div>
                        <div>
                            <ControlledInputField
                                control={formController.control}
                                name="lng"
                                label="Longitude"
                                data-testid="long-input"
                                placeholder="e.g. -74.0445"
                                type="number"
                                asterix
                                required
                                maxLength={11}
                                rules={{
                                    pattern: {
                                        value: LONG_REGEX,
                                        message: COORDINATES_ERROR_MESSAGES.lng,
                                    },
                                    validate: enable_tf_error_outside_usa
                                        ? coordinatesValidator
                                        : undefined,
                                }}
                                globalError={outsideUSError}
                            />
                        </div>
                        <ControlledInputField
                            control={formController.control}
                            name="address"
                            label="Address"
                            data-testid="address-input"
                            placeholder="Address"
                            maxLength={150}
                        />
                    </div>
                </FlexContainer>
                <div className={styles.calloutContainer}>
                    <Callout
                        testId="request-traffic-pin-callout"
                        text="To learn how to capture the lat/long coordinates for a traffic pin"
                        FreeTextIcon={<UtilityInfoIcon />}
                        CTA={
                            <>
                                <Link
                                    target="_blank"
                                    href="https://view-su2.highspot.com/viewer/66883b3ab40123442b927e35"
                                >
                                    click here
                                </Link>
                            </>
                        }
                    />
                </div>
            </div>
        </SupportRequestModalForm>
    );
};
